import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

/**
 * The 6-box code field.
 *
 * ONE real `<input>` behind six drawn boxes, not six inputs. Six inputs is the
 * obvious shape and it is the wrong one: iOS delivers the whole code to a
 * single field (its "From Mail" banner and the QuickType bar both do), and so
 * does a paste from the mail app. Splitting the field means writing the
 * distribution, the backspace walk and the caret restore by hand, and getting
 * autofill wrong on the platform this screen was drawn for.
 *
 * UI machinery, so `useState`/`useRef` live here rather than in the two
 * screens that mount it.
 *
 * Two drawings of the same field. `mobile` is the carousel's and the mobile
 * login's. `desktop` is the auth gate modal's (Figma "D7.inscription.default"):
 * taller boxes, and an answered box filled in moss as well as outlined.
 */
export function OtpCodeInput({
  value,
  length,
  onChange,
  onComplete,
  hasError,
  autoFocus,
  disabled,
  variant,
}: {
  value: string;
  length: number;
  onChange: (value: string) => void;
  /** Fired the moment the field holds a full code, so the user does not have to
   * reach for a Continue button after typing the last digit. */
  onComplete: (value: string) => void;
  hasError: boolean;
  autoFocus: boolean;
  disabled: boolean;
  variant: "mobile" | "desktop";
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setFocused] = useState(false);

  // A disabled field drops its focus, and the hosts disable this one while the
  // code is being sent, which is the moment it mounts. `autoFocus` alone
  // therefore left the caret nowhere: take it back once the field is usable.
  useEffect(() => {
    if (!autoFocus || disabled) return;
    const input = inputRef.current;
    if (input !== null) input.focus();
  }, [autoFocus, disabled]);

  const digits = Array.from({ length }, (_, index) => {
    const digit = value.at(index);
    return digit === undefined ? "" : digit;
  });
  // The caret sits on the first empty box, or on the last one when full.
  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div
      className="relative w-full"
      onClick={() => {
        const input = inputRef.current;
        if (input !== null) input.focus();
      }}
    >
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        // What raises the iOS "From Mail" banner. iOS reads the code from the
        // body of the OTP mail, and only from mail in Apple Mail.
        autoComplete="one-time-code"
        // Not `type="number"`: it strips leading zeros and shows spinners.
        pattern="[0-9]*"
        maxLength={length}
        value={value}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-label="Verification code"
        // Hidden by transparency, never by `opacity-0`: iOS gives a fully
        // transparent field no edit menu, so a long press offers no Paste and a
        // code copied from the mail cannot land. The text, caret and selection
        // go transparent instead, and the boxes show through. Autofill is the
        // exception: WebKit paints its fill with a rule a background class
        // cannot override, and an inset shadow would cover the gaps between
        // the boxes, so an autofilled field alone takes `opacity-0`. It
        // already holds the code, so it loses no Paste worth having.
        className="absolute inset-0 size-full border-0 bg-transparent text-transparent caret-transparent [-webkit-text-fill-color:transparent] selection:bg-transparent autofill:opacity-0 focus:outline-none"
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onChange={(event) => {
          const next = event.target.value.replace(/\D/g, "").slice(0, length);
          onChange(next);
          if (next.length === length) onComplete(next);
        }}
      />

      <div className="flex gap-2 justify-between pointer-events-none">
        {digits.map((digit, index) => (
          <div
            key={index}
            className={cn(
              "flex flex-1 items-center justify-center",
              // Same border/radius/fill idiom as the `Input` the neighbouring
              // slides use, so the six boxes read as one family with them.
              "rounded-xl border border-solid bg-white",
              "font-dm-sans",
              "transition-colors duration-150 ease-out",
              variant === "mobile"
                ? cn(
                    "h-14 text-heading-lg font-semibold",
                    hasError
                      ? "border-ds-strawberry-600 text-ds-black"
                      : // A box that holds a digit is answered: it takes the
                        // brand green, borders and all, so the code reads as
                        // accepted as it is typed rather than only once it is
                        // submitted.
                        digit !== ""
                        ? "border-ds-primary text-ds-primary"
                        : isFocused && index === activeIndex && !disabled
                          ? "border-ds-black text-ds-black"
                          : "border-ds-slate-300 text-ds-black"
                  )
                : cn(
                    "h-17 text-desktop-heading font-bold",
                    hasError
                      ? "border-2 border-ds-strawberry-700 bg-ds-moss-50 text-ds-strawberry-700"
                      : digit !== ""
                        ? "border-2 border-ds-mint-700 bg-ds-moss-50 text-ds-mint-700"
                        : isFocused && index === activeIndex && !disabled
                          ? "border-ds-black text-ds-black"
                          : "border-ds-slate-200 text-ds-black"
                  ),
              disabled ? "opacity-60" : ""
            )}
          >
            {digit}
          </div>
        ))}
      </div>
    </div>
  );
}
